/**
 * Localized clinic identity used to auto-fill page titles, meta descriptions,
 * structured data and the sitemap-facing SEO layer.
 *
 * NAP values (name / address / phone) come from `clinicSchema.ts` (single
 * source of truth); the language-dependent labels come from the translation
 * files, so adding a language automatically localizes the clinic block too.
 */
import { translations } from '@/translations';
import {
  CLINIC_BRAND,
  CLINIC_CLOSES,
  CLINIC_EMAIL,
  CLINIC_LOCATIONS,
  CLINIC_OPENING_HOURS,
  CLINIC_OPENS,
  CLINIC_PRIMARY_PHONE,
  CLINIC_PRIMARY_PHONE_HUMAN,
  CLINIC_SITE_URL,
} from '@/lib/clinicSchema';

export type ClinicLang = keyof typeof translations;

const PRIMARY = CLINIC_LOCATIONS[0];

export interface LocalizedClinicInfo {
  brand: string;
  city: string;
  country: string;
  region: string;
  streetAddress: string;
  postalCode: string;
  addressCountry: string;
  fullAddress: string;
  phone: string;
  phoneHuman: string;
  email: string;
  siteUrl: string;
  /** Machine readable opening hours, e.g. "Mo-Su 10:00-18:00". */
  openingHours: string;
  /** Localized label, e.g. "Openingstijden". */
  hoursLabel: string;
  /** Localized human sentence, e.g. "Elke dag 10:00 – 18:00". */
  hoursHuman: string;
  /** Short "Brand City" suffix appended to titles when it fits. */
  titleSuffix: string;
  /** One-line NAP sentence appended to meta descriptions when it fits. */
  napSentence: string;
}

type ClinicTranslation = {
  city: string;
  country: string;
  region: string;
  hoursLabel: string;
  hoursDaily: string;
};

const FALLBACK: ClinicTranslation = {
  city: 'Den Haag',
  country: 'Netherlands',
  region: 'Zuid-Holland',
  hoursLabel: 'Opening hours',
  hoursDaily: 'Every day',
};

const readClinicTranslation = (lang: string): ClinicTranslation => {
  const dict = (translations as Record<string, { clinic?: Partial<ClinicTranslation> }>)[lang];
  const clinic = dict?.clinic ?? {};
  return {
    city: clinic.city ?? FALLBACK.city,
    country: clinic.country ?? FALLBACK.country,
    region: clinic.region ?? FALLBACK.region,
    hoursLabel: clinic.hoursLabel ?? FALLBACK.hoursLabel,
    hoursDaily: clinic.hoursDaily ?? FALLBACK.hoursDaily,
  };
};

export const getClinicInfo = (lang: string): LocalizedClinicInfo => {
  const t = readClinicTranslation(lang);
  const fullAddress = `${PRIMARY.streetAddress}, ${PRIMARY.postalCode ?? ''} ${PRIMARY.addressLocality}`.replace(/\s+/g, ' ').trim();
  const hoursHuman = `${t.hoursDaily} ${CLINIC_OPENS} – ${CLINIC_CLOSES}`;
  return {
    brand: CLINIC_BRAND,
    city: t.city,
    country: t.country,
    region: t.region,
    streetAddress: PRIMARY.streetAddress,
    postalCode: PRIMARY.postalCode ?? '',
    addressCountry: PRIMARY.addressCountry,
    fullAddress,
    phone: CLINIC_PRIMARY_PHONE,
    phoneHuman: CLINIC_PRIMARY_PHONE_HUMAN,
    email: CLINIC_EMAIL,
    siteUrl: CLINIC_SITE_URL,
    openingHours: CLINIC_OPENING_HOURS,
    hoursLabel: t.hoursLabel,
    hoursHuman,
    titleSuffix: `${CLINIC_BRAND} ${t.city}`,
    napSentence: `${CLINIC_BRAND}, ${fullAddress} · ${CLINIC_PRIMARY_PHONE_HUMAN} · ${hoursHuman}`,
  };
};

const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;

/**
 * Appends the clinic city to a page title when the title does not already
 * mention it and the result stays inside the SERP title budget.
 */
export const decorateSeoTitle = (title: string, lang: string, max = TITLE_MAX): string => {
  const info = getClinicInfo(lang);
  const base = title.trim();
  if (!base) return info.titleSuffix;
  const lower = base.toLowerCase();
  if (lower.includes(info.city.toLowerCase())) return base;
  const candidate = `${base} – ${info.city}`;
  return candidate.length <= max ? candidate : base;
};

/**
 * Appends the clinic NAP line to a meta description when it fits within the
 * SERP snippet budget, so every page carries consistent local-SEO signals.
 */
export const decorateSeoDescription = (description: string, lang: string, max = DESCRIPTION_MAX): string => {
  const info = getClinicInfo(lang);
  const base = description.trim();
  if (!base) return info.napSentence.slice(0, max);
  if (base.toLowerCase().includes(info.streetAddress.toLowerCase())) return base;
  const candidate = `${base.replace(/\s+$/, '')} ${info.napSentence}`;
  return candidate.length <= max ? candidate : base;
};
